/**
 * A face check task, as this module reads it: the four fields it actually consumes, and nothing
 * else.
 *
 * **Declared here rather than aliased to the generated schema type.** An alias would read as the
 * safer choice, on the grounds that the fields then cannot drift from the endpoint. It is not. The
 * generated type is regenerated from whatever spec the build is pointed at, so aliasing it makes a
 * backend field rename a compile error in a reducer that never needed to know the field existed,
 * and it drags this module's meaning into a file that is explicitly marked for deletion. What this
 * module needs is a stable statement of what it reads. If the endpoint renames a field, the fix
 * belongs at the call site that maps the response in, which is one place and an obvious one.
 *
 * Every field is optional because the generated schemas are `.partial()`: the endpoint promises
 * none of them individually. `isOpen` and `requestStateFromTasks` are written to survive each one
 * being absent, and a task with no status is treated as not open, which is the safe direction for
 * a control that emails a customer.
 */
export type TFaceCheckTask = {
  /** `ACTIVE`, `INACTIVE`, `SUCCEEDED` and so on. See `OPEN_STATUSES`. */
  status?: string;
  /** The link a client sends their customer to complete the check. */
  trackingReferenceUrl?: string;
  /** ISO timestamp. Orders the tasks and provides the `Requested on` provenance. */
  createdDateTime?: string;
  /** Currently a Cognito sub rather than an email. */
  createdBy?: string;
};

/**
 * What the cards and the evidence dialog need to know about the customer's face check requests.
 *
 * One object rather than four separate derivations, because every field answers the same question
 * and they must agree: the verb on the button, the presence of the link and the provenance row all
 * describe one request history, and computing them apart is how they contradict each other.
 */
export type TRequestState = {
  /** Drives the button's verb: `REQUEST AGAIN` when true, `REQUEST NOW` when false. */
  hasOutstandingRequest: boolean;
  /** The link a client sends their customer. Absent when nothing is open. */
  requestUrl?: string;
  /** When the most recent request was raised, open or not. */
  requestedOn?: string;
  /** Who raised the most recent request. Currently a Cognito sub, not an email. */
  requestedBy?: string;
};

/**
 * The statuses that mean a task is still open, as an **allowlist**.
 *
 * Deliberately not "anything that is not `SUCCEEDED`". A `FAILED` task is not something the customer
 * can still complete, so counting it as outstanding would offer `REQUEST AGAIN` for a repeat that
 * cannot happen. The allowlist also survives the enum growing: a status nobody has designed for is
 * treated as not-open, which is the safe direction for a control that emails a customer when
 * pressed.
 *
 * `ACTIVE` means the customer has engaged with the task, `INACTIVE` that they have not yet. Both are
 * open. Verified against person-api `main` on 7 August: only `ACTIVE` and `SUCCEEDED` are ever
 * written on this path, so today the two definitions select the same tasks; the allowlist is chosen
 * for what happens when that stops being true.
 */
const OPEN_STATUSES: ReadonlySet<string> = new Set(['ACTIVE', 'INACTIVE']);

function isOpen(task: TFaceCheckTask): boolean {
  return OPEN_STATUSES.has(task.status);
}

/**
 * Most recent first. The endpoint promises no order, so anything that reads `tasks[0]` is reading
 * whichever row the database happened to return first.
 */
function newestFirst(tasks: TFaceCheckTask[]): TFaceCheckTask[] {
  return [...tasks].sort(
    (a, b) => new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime(),
  );
}

/**
 * Reduces the customer's face check tasks to the request state the UI renders.
 *
 * **Outstanding is a question about the set, not about the latest task.** A customer can hold
 * several at once, and they close together when a check is confirmed, so a single open task behind a
 * newer closed one still means a request is outstanding.
 *
 * **Provenance comes from the latest task whatever its status**, matching what `EvidenceProvenance`
 * states: a customer asked once and long since finished still has a `Requested on` and a
 * `Requested by`, even though nothing is open and the button reads `REQUEST NOW`.
 */
export function requestStateFromTasks(tasks: TFaceCheckTask[]): TRequestState {
  const ordered = newestFirst(tasks);
  const latest = ordered[0];
  const latestOpen = ordered.find(isOpen);

  return {
    hasOutstandingRequest: Boolean(latestOpen),
    requestUrl: latestOpen?.trackingReferenceUrl,
    requestedOn: latest?.createdDateTime,
    requestedBy: latest?.createdBy,
  };
}
